import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  calculateAge,
  formatDate,
  getCurrentDateTime,
  getISTDate,
  getKeyPathValue,
  request,
  toast
} from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { PATIENT_TYPES, REPORT_TYPES } from '../../../../../constants/dropdown';
import SearchIcon from '@mui/icons-material/Search';
import { IPDSearchHandler } from '../../HMS/transactions/utils';
import { debounce } from 'lodash';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const RESULT_TABLE_HEADER = [
  {
    header: 'Res No',
    accessor: 'report_id'
  },
  {
    header: 'Res Date',
    accessor: (row) => formatDate(row.createdAt)
  },
  {
    header: 'Test Name',
    accessor: 'test_names'
  }
];

const RADIOLOGY_TABLE_HEADER = [
  {
    header: 'Report No',
    accessor: 'report_no'
  },
  {
    header: 'Report Date',
    accessor: (row) => formatDate(row.date)
  },
  {
    header: 'Service Name',
    accessor: (row) => row?.service_name?.service_name
  }
];

const TEST_ITEM_TABLE_HEADER = [
  {
    header: 'Auth. Doctor',
    accessor: 'sign_off_by'
  },
  {
    header: 'Test Item Name',
    accessor: 'test_item_names'
  },
  {
    header: 'Result',
    accessor: 'Result'
  },
  {
    header: 'Unit',
    accessor: 'unit'
  },
  {
    header: 'Range From',
    accessor: 'general_from'
  },
  {
    header: 'Range To',
    accessor: 'general_to'
  },
  {
    header: 'Pre. Result 1',
    accessor: 'pre_result_1'
  },
  {
    header: 'Pre. Result 2',
    accessor: 'pre_result_2'
  },
  {
    header: 'Pre. Result 3',
    accessor: 'pre_result_3'
  }
];

const PATIENT_TABLE_COLUMNS = [
  { header: 'UHID No.', accessor: 'uhid' },
  { header: 'OP No.', accessor: 'op_no' },
  { header: 'Date', accessor: (row) => `${getCurrentDateTime('date', row?.date)}` },
  {
    header: 'Patient name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row?.name_suffix} ${row?.first_name} ${row?.last_name ? row?.last_name : ''}`}
        </span>
      );
    }
  },
  { header: 'Guardian', accessor: 'guardian_name' },
  { header: 'Mobile No.', accessor: 'address.mobile' },
  {
    header: 'State Name',
    accessor: (row) => {
      const state = row?.address?.state;
      return typeof state === 'object' ? state?.state : state || 'N/A';
    }
  },
  { header: 'Aadhar No.', accessor: 'address.aadhar' },
  { header: 'Doctor Name', accessor: 'consultant.name' },
  { header: 'Recd Amount', accessor: 'recd' },
  { header: 'Sponsor Name', accessor: 'sponsor.name' },
  { header: 'Address', accessor: 'address.address' },
  { header: 'Status', accessor: 'status', sx: { textTransform: 'uppercase' } }
];

const initialOPDBillingDataObj = {
  data: [],
  loading: false
};

const initialPatientDataObj = {
  data: [],
  loading: false
};

export const ViewResult = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();
  const [patientDataObj, setPatientDataObj] = useState(initialPatientDataObj);
  const [openPatientQueryDialog, setOpenPatientQueryDialog] = useState(false);
  const [input, setInput] = useState('');
  const user = JSON.parse(localStorage.getItem('user'));
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [patientInput, setPatientInput] = useState('');
  const [IPDSearchDialogOpen, setIPDSearchDialogOpen] = useState(false);
  const [resultEntryData, setResultEntryData] = useState([]);
  const [radiologyProcedureData, setRadiologyProcedureData] = useState([]);
  const [debouncedInput, setDebouncedInput] = useState('');
  const[editorData,setEditorData] = useState('')

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        uhid: Yup.string().required(' '),
        patient_name: Yup.string().required(' '),
        opd_type: Yup.string().required(' '),
        ref_type: Yup.string().required(' '),
        doctor: Yup.string().required(' '),
        sponsor: Yup.string().required(' '),
        department: Yup.string().required(' '),
        bill: Yup.object().shape({
          rec_amt_1: Yup.number().min(0, ' '),
          rec_amt_2: Yup.number().min(0, ' '),
          pay_mode: Yup.string().required(' ')
        })
      })
    ),
    defaultValues: {
      bill: {
        date: getISTDate(),
        time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
        pay_mode: 'CASH',
        out_sourced: 'No',
        tds_amt: 0,
        disc_per: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'service_items',
        'uhid'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('uhid', selectedRecord.uhid);
      const formattedbillDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('bill.date', formattedbillDate);
      setValue('bill.time', selectedRecord?.time);
      setDetailList(selectedRecord?.service_items);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const handleRowDelete = (rowId) => {
    setDetailList((prev) => prev.filter((data, index) => index !== rowId));
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };

  const handlePatientRegisteration = async () => {
    setOpenPatientQueryDialog(true);
    setPatientDataObj({ loading: true });
    try {
      const response = await request('/patient-registeration-list', { method: 'GET' });
      if (response.success) {
        const sortedData = response.patientData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const filteredData = sortedData.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setPatientDataObj({ loading: false, data: filteredData });
      } else {
        toast('Error in fetchingggggggg data', { variant: 'error' });
        setPatientDataObj({ loading: false });
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast('Error in fetching data', { variant: 'error' });
      setPatientDataObj({ loading: false });
    }
  };

  useEffect(() => {
    handleUhidSearch();
  }, [patientInput]);

  const handleUhidSearch = async () => {
    const lowercasedInput = patientInput.toLowerCase().trim();
    try {
      const query = {};
      if (startDateInput && endDateInput) {
        query.startDate = startDateInput;
        query.endDate = endDateInput;
      }
      if (lowercasedInput) {
        query.name = lowercasedInput;
      }
      const response = await request('/patient-registeration-list', {
        method: 'GET',
        query: query
      });
      if (response.success) {
        const sortedData = response.patientData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const filteredData = sortedData.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setPatientDataObj({ loading: false, data: filteredData });
      } else {
        toast('Error in fetching dataaaaa', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
    }
  };

  const handlePatientChange = (e) => {
    setPatientInput(e.target.value);
  };
  const handleStartChangeDate = (e) => {
    setStartDateInput(e.target.value);
  };

  const handleEndChangeDate = (e) => {
    setEndDateInput(e.target.value);
  };

  const handleSearchByDate = async () => {
    setPatientDataObj({ loading: true, data: [] });
    try {
      const query = {};
      if (startDateInput && endDateInput) {
        query.startDate = startDateInput;
        query.endDate = endDateInput;
      }
      const response = await request('/patient-registeration-list', {
        method: 'GET',
        query: query
      });
      if (response.success) {
        const sortedData = response.patientData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const filteredData = sortedData.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setPatientDataObj({ loading: false, data: filteredData });
      } else {
        toast('Error in fetching dataaaaa', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
    }
  };

  const searchIPDDetails = async (ipd_no) => {
    setLoading(true);
    const response = await request('/indoor-registeration-detail', {
      method: 'GET',
      params: ipd_no
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        const { data: patientData } = response;
        setValue('adm_no', ipd_no);
        setValue('patient_name', `${patientData?.patient?.first_name} ${patientData?.patient?.last_name}`);
        setValue('patient_suffix', patientData?.patient?.name_suffix);
        setValue('ward', patientData?.consultant_detail.room?.ward?.name);
        setValue('uhid', patientData?.uhid);
        setValue('doctor', patientData.consultant_detail?.doctor?.name);
        setValue('room_no', patientData.consultant_detail?.room?.room_no);
        setValue('bed_no', patientData.consultant_detail?.bed_no);
        setValue('sponsor', patientData.patient.sponsor?.name);
        setValue('gender', patientData?.patient?.gender);
        setValue('age', calculateAge(patientData?.patient?.dob));
        setValue('address', patientData?.patient?.address.address);
        setValue('mobile', patientData?.patient.address?.mobile);
        handleResultEntryDataIpd(patientData?.ipd_no);
        fetchRadiologyProcedureReportIpd(patientData?.ipd_no);
      }
    }
    setLoading(false);
  };

  const handleResultEntryData = async (uhid) => {
    setResultEntryData({ loading: true });
    try {
      const response = await request('/lis-result-entry-single-patient-list', {
        method: 'GET',
        query: { uhid }
      });
      if (response?.success && response?.data) {
        setResultEntryData({ loading: false, data: response.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setResultEntryData({ loading: false });
      }
    } catch (error) {
      console.error('Request failed:', error);
      toast('Something went wrong', { variant: 'error' });
      setResultEntryData({ loading: false });
    }
  };

  const handleResultEntryDataIpd = async (ipd_no) => {
    setResultEntryData({ loading: true });
    try {
      const response = await request('/lis-result-entry-single-patient-ipd-list', {
        method: 'GET',
        query: { ipd_no }
      });
      if (response?.success && response?.data) {
        setResultEntryData({ loading: false, data: response.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setResultEntryData({ loading: false });
      }
    } catch (error) {
      console.error('Request failed:', error);
      toast('Something went wrong', { variant: 'error' });
      setResultEntryData({ loading: false });
    }
  };

  useEffect(() => {
    const debounced = debounce(() => setDebouncedInput(input), 300);
    debounced();
    return () => debounced.cancel();
  }, [input]);

  const filteredData = useMemo(() => {
    if (!input?.trim()) return resultEntryData.data || [];
    const lowerInput = input.toLowerCase();
    return (resultEntryData.data || []).filter((entry) => entry.test_names?.toLowerCase().includes(lowerInput));
  }, [input, resultEntryData.data]);

  const fetchRadiologyProcedureReport = async (uhid) => {
    setRadiologyProcedureData({ loading: true });
    const response = await request('/radiology-procedure-report-list', { method: 'GET', query: { uhid } });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRadiologyProcedureData({ loading: false, data: sortedData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setRadiologyProcedureData({ loading: false });
    }
  };

  const fetchRadiologyProcedureReportIpd = async (ipd_no) => {
    setRadiologyProcedureData({ loading: true });
    const response = await request('/radiology-procedure-report-ipd-list', { method: 'GET', query: { ipd_no } });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRadiologyProcedureData({ loading: false, data: sortedData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setRadiologyProcedureData({ loading: false });
    }
  };
  
  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setEditorData(data);
  };


  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                View Result
              </Typography>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Type</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="type" menus={PATIENT_TYPES} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Mobile</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="mobile" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Address</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="address" />
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>UHID</Typography>
                    <TextField sx={{ flex: 0.7 }} name="uhid" disabled />
                    <Button
                      sx={{
                        flex: 0.1,
                        minWidth: 0,
                        p: 0.25,
                        height: '100%',
                        position: 'relative'
                      }}
                      variant="contained"
                      onClick={() => {
                        const type = watch('type');
                        if (type === 'OPD') {
                          handlePatientRegisteration();
                        } else if (type === 'IPD') {
                          setIPDSearchDialogOpen(true);
                        }
                      }}
                    >
                      <SearchIcon sx={{ fontSize: '2rem' }} />
                    </Button>
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Adm. No.</Typography>
                    <TextField disabled name="adm_no" sx={{ flex: 0.8 }} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Age/Gender</Typography>
                    <TextField disabled sx={{ flex: 0.4 }} name="age" />
                    <TextField disabled sx={{ flex: 0.4 }} name="gender" />
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Report Type</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="report_type" menus={REPORT_TYPES} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Patient Name</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="patient_name" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Doctor Name</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="doctor" />
                  </Stack>
                </Grid>
                {watch('report_type') === 'LAB' && (
                  <>
                    <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
                        <MuiTextField
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              padding: '0',
                              fontSize: '14px'
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 14px'
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1.2rem',
                              fontWeight: 700
                            }
                          }}
                          label="Search Test"
                          variant="outlined"
                          value={input}
                          onChange={handleChange}
                        />
                      </Stack>
                      <Table
                        containerSX={{ flex: 1 }}
                        data={filteredData || []}
                        columns={RESULT_TABLE_HEADER}
                        size="small"
                        onRowClick={(row) => {
                          const enrichedTests = (row.tests || []).map((test) => ({
                            ...test,
                            sign_off_by: row.sign_off_by || null
                          }));
                          setDetailList(enrichedTests);
                        }}
                      />
                    </Grid>

                    <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Table
                        containerSX={{ flex: 1, marginTop: '1.1%' }}
                        data={detailList.map((data, index) => ({ _id: index, ...data }))}
                        columns={TEST_ITEM_TABLE_HEADER}
                        size="small"
                        handleDeleteRow={handleRowDelete}
                      />
                    </Grid>
                  </>
                )}

                {watch('report_type') === 'RADIOLOGY' && (
                  <>
                    <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
                        <MuiTextField
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              padding: '0',
                              fontSize: '14px'
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 14px'
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1.2rem',
                              fontWeight: 700
                            }
                          }}
                          label="Search Service"
                          variant="outlined"
                          value={input}
                          onChange={handleChange}
                        />
                      </Stack>
                      <Table
                        containerSX={{ flex: 1 }}
                        data={radiologyProcedureData?.data || []}
                        columns={RADIOLOGY_TABLE_HEADER}
                        size="small"
                        onRowClick={(row) => {
                          console.log(row, 'HELLO ROW IS HERE !');
                          setValue('diagnosis', row?.diagnosis_name?.diagnosis);
                          setValue('rep_doctor', row?.rep_doctor?.doctor);
                          setEditorData(row?.editorContent)
                        }}
                      />
                    </Grid>

                    <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', gap: 1 ,marginTop:"2%"}}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Diagnosis</Typography>
                        <TextField disabled sx={{ flex: 0.8 }} name="diagnosis" />
                      </Stack>

                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Rep. Doctor</Typography>
                        <TextField disabled sx={{ flex: 0.8 }} name="rep_doctor" />
                      </Stack>

                      <CKEditor
                          editor={ClassicEditor}
                          disabled
                          data={Array.isArray(editorData) ? editorData.join(' ') : editorData || ''}
                          onReady={(editor) => {}}
                          onChange={handleEditorChange}
                          onBlur={(event, editor) => {
                            console.log('Blur.', editor);
                          }}
                          onFocus={(event, editor) => {
                            console.log('Focus.', editor);
                          }}
                        />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openPatientQueryDialog}
        onClose={() => {
          setOpenPatientQueryDialog(false);
          setStartDateInput('');
          setEndDateInput('');
        }}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient Registeration Query Window
          </Typography>
          <IconButton
            onClick={() => {
              setOpenPatientQueryDialog(false);
              setStartDateInput('');
              setEndDateInput('');
            }}
          >
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 10px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem'
              }
            }}
            label="Search Patient"
            placeholder="Search Patient"
            variant="outlined"
            value={patientInput}
            onChange={handlePatientChange}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{
            mt: 1,
            mb: 1,
            width: '60%',
            gap: 2,
            justifyContent: 'space-between'
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontWeight: 700,
              fontSize: '1.4rem',
              textAlign: 'center' 
            }}
          >
            From Date
          </Typography>

          <MuiTextField
            sx={{
              flex: 2,
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' 
              }
            }}
            variant="outlined"
            name="from_date"
            type="date"
            onChange={handleStartChangeDate}
            value={startDateInput}
          />

          <Typography
            sx={{
              flex: 1,
              fontWeight: 700,
              fontSize: '1.4rem',
              textAlign: 'center' 
            }}
          >
            To Date
          </Typography>

          <MuiTextField
            sx={{
              flex: 2,
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem'
              }
            }}
            name="to_date"
            variant="outlined"
            type="date"
            onChange={handleEndChangeDate}
            value={endDateInput}
          />
          <Button variant="contained" disabled={!startDateInput || !endDateInput} onClick={handleSearchByDate}>
            Search
          </Button>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={PATIENT_TABLE_COLUMNS}
          data={patientDataObj?.data || []}
          loading={patientDataObj.loading}
          onRowClick={(row) => {
            reset();
            setOpenPatientQueryDialog(false);
            setValue(
              'patient_name',
              `${row?.name_suffix || ''} ${row?.first_name || ''} ${row?.last_name || ''}`.trim()
            );
            if (row?.dob) {
              setValue('age', calculateAge(row?.dob));
            }
            setValue('mobile', row?.address?.mobile);
            setValue('gender', row?.gender);
            setValue('doctor', row?.consultant?.name);
            setValue('uhid', row?.uhid);
            setValue('address', row?.address?.address);
            handleResultEntryData(row?.uhid);
            fetchRadiologyProcedureReport(row?.uhid);
          }}
        />
      </Dialog>
      <Dialog
        PaperProps={{ sx: { p: 1 } }}
        open={IPDSearchDialogOpen}
        onClose={() => setIPDSearchDialogOpen(false)}
        scroll="body"
        maxWidth="xl"
      >
        <IPDSearchHandler onClose={() => setIPDSearchDialogOpen(false)} onAdd={searchIPDDetails} />
      </Dialog>
    </>
  );
};
